import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  createMontyPaySession,
  storePaymentSession,
  CreateSessionRequest,
} from '../../services/montyPayService.ts';

interface MontyPayCheckoutProps {
  amount: number;
  currency?: string;
  description: string;
  metadata?: Record<string, any>;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  buttonText?: string;
  buttonClassName?: string;
}

export const MontyPayCheckout: React.FC<MontyPayCheckoutProps> = ({
  amount,
  currency = 'USD',
  description,
  metadata = {},
  customerName,
  customerEmail,
  customerPhone,
  onSuccess,
  onError,
  buttonText = 'Pay Now',
  buttonClassName = 'bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-blue-700',
}) => {
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    try {
      setLoading(true);

      // Get auth token
      const authToken = sessionStorage.getItem('authToken');
      if (!authToken) {
        toast.error('Please login to continue');
        return;
      }

      // Get user info from session storage or props
      const userEmail = customerEmail || sessionStorage.getItem('userEmail');
      const userName = customerName || userEmail?.split('@')[0] || 'Customer';
      const orgId = sessionStorage.getItem('orgId');

      if (!userEmail) {
        toast.error('Email is required for payment');
        return;
      }

      // Store auth token and orgId in localStorage for cross-tab access
      localStorage.setItem('montypay_auth_token', authToken);
      localStorage.setItem('montypay_user_email', userEmail);
      if (orgId) {
        localStorage.setItem('montypay_org_id', orgId);
      }

      // Prepare payment session data
      const sessionData: CreateSessionRequest = {
        amount,
        currency,
        customer: {
          name: userName,
          email: userEmail,
          ...(customerPhone && { phone: customerPhone }),
        },
        return_url: `${window.location.origin}/payment/success`,
        cancel_url: `${window.location.origin}/payment/cancel`,
        description,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      };

      // Create payment session
      const response = await createMontyPaySession(sessionData, authToken);

      if (response.success) {
        // Store session data for later verification
        storePaymentSession(response.session_id, response.order_id);

        // // Call success callback before redirect
        if (onSuccess) {
          onSuccess();
        }
        // window.location.href = response.redirect_url;
        // Open MontyPay checkout in a new tab
        window.open(response.redirect_url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Failed to create payment session');
      }
    } catch (error: any) {
      console.error('MontyPay checkout error:', error);
      
      const errorMessage = error.hint || error.message || 'Failed to initiate payment';
      toast.error(errorMessage);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleCheckout}
      disabled={loading}
      className={buttonClassName}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Processing...
        </span>
      ) : (
        buttonText
      )}
    </button>
  );
};

export default MontyPayCheckout;
